<template>
  <div
    class="product-cards-widget"
    :class="[`style-${widget.cardStyle || 'default'}`, widgetClasses]"
    :style="widgetStyles"
  >
    <!-- Widget Title -->
    <div v-if="widget.title" class="widget-header mb-4">
      <h3 class="widget-title">{{ widget.title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state text-center py-4">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Φόρτωση...</span>
      </div>
      <p class="mt-2 text-muted">Φόρτωση προϊόντων...</p>
    </div>

    <!-- Products Grid -->
    <div v-else-if="products.length > 0" class="products-grid">
      <div class="row g-4" :class="`row-cols-1 row-cols-md-${widget.columns || 3}`">
        <div v-for="product in products" :key="product.id" class="col">
          <article class="product-card h-100">
            <!-- Product Image -->
            <div v-if="widget.showImage !== false" class="product-image-container">
              <img 
                :src="getProductImageUrl(product.image)"
                :alt="product.name"
                class="product-image"
                loading="lazy"
                decoding="async"
                @error="handleImageError"
              >
              
              <!-- Product Badges -->
              <div class="product-badges">
                <span v-if="product.sale_price && product.sale_price < product.price" class="badge badge-sale">
                  ΠΡΟΣΦΟΡΑ
                </span>
                <span v-if="product.stock <= 0" class="badge badge-out-of-stock">
                  ΕΞΑΝΤΛΗΘΗΚΕ
                </span>
              </div>

              <!-- Product Action Buttons -->
              <div class="product-action-buttons">
                <button 
                  @click.prevent="toggleWishlist(product)"
                  class="btn-icon wishlist-btn"
                  :class="{ 'active': isInWishlist(product.id) }"
                  :title="isInWishlist(product.id) ? 'Αφαίρεση από wishlist' : 'Προσθήκη στο wishlist'"
                >
                  <i class="bi" :class="isInWishlist(product.id) ? 'bi-heart-fill' : 'bi-heart'"></i>
                </button>
                <button 
                  @click.prevent="quickView(product)"
                  class="btn-icon quick-view-btn"
                  title="Γρήγορη προβολή"
                >
                  <i class="bi bi-eye"></i>
                </button>
              </div>

              <!-- Category Badge -->
              <div v-if="widget.showCategory !== false && product.category_name" class="product-category-badge">
                {{ product.category_name }}
              </div>
            </div>

            <!-- Product Content -->
            <div class="product-content">
              <h4 class="product-title">
                <router-link :to="`/products/${product.id}`" class="product-link">
                  {{ product.name }}
                </router-link>
              </h4>

              <!-- Product Meta -->
              <div v-if="widget.showBrand !== false && product.brand_name" class="product-meta">
                <span class="product-brand">
                  <i class="bi bi-award me-1"></i>
                  {{ product.brand_name }}
                </span>
              </div>

              <!-- Product Description -->
              <p v-if="widget.showDescription !== false && product.description" class="product-description">
                {{ product.description }}
              </p>

              <!-- Product Price -->
              <div v-if="widget.showPrice !== false" class="product-pricing">
                <div v-if="product.sale_price && product.sale_price < product.price" class="price-container">
                  <span class="sale-price">€{{ formatPrice(product.sale_price) }}</span>
                  <span class="original-price">€{{ formatPrice(product.price) }}</span>
                </div>
                <div v-else class="price-container">
                  <span class="current-price">€{{ formatPrice(product.price) }}</span>
                </div>
              </div>

              <!-- Add to Cart Button -->
              <div v-if="widget.showAddToCart !== false" class="product-actions">
                <button 
                  v-if="product.stock > 0"
                  @click="addToCart(product)"
                  class="btn btn-primary btn-sm"
                  :disabled="addingToCart === product.id"
                >
                  <span v-if="addingToCart === product.id">
                    <i class="bi bi-arrow-repeat spinner"></i>
                    Προσθήκη...
                  </span>
                  <span v-else>
                    <i class="bi bi-cart-plus me-1"></i>
                    Στο καλάθι
                  </span>
                </button>
                <button v-else class="btn btn-secondary btn-sm" disabled>
                  <i class="bi bi-x-circle me-1"></i>
                  Εξαντλημένο
                </button>
              </div>
            </div>
          </article>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state text-center py-4">
      <i class="bi bi-box-seam display-4 text-muted mb-3"></i>
      <h5 class="text-muted">Δεν βρέθηκαν προϊόντα</h5>
      <p class="text-muted mb-0">
        {{ getEmptyStateMessage() }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import type { ProductCardsWidget } from '@/types/pagebuilder'
import { API_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'
import { UPLOADS_BASE_URL } from '@/config/env'
import { useCartStore } from '@/stores/cart'
import { useWishlistStore } from '@/stores/wishlist'
import { settingsService } from '@/services/api'
import { useRouter } from 'vue-router'
import { useWidgetStyles } from '@/composables/useWidgetStyles'

interface Props {
  widget: ProductCardsWidget
}

interface Product {
  id: number
  name: string
  description: string
  price: number
  sale_price?: number
  image: string
  category_id: number
  category_name: string
  brand_id?: number
  brand_name?: string
  stock: number
  is_featured: boolean
}

const props = defineProps<Props>()
const { widgetStyles, widgetClasses } = useWidgetStyles(props.widget)
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const router = useRouter()

// State
const products = ref<Product[]>([])
const loading = ref(false)
const navSettings = ref<any>({})

// Computed tooltip colors from navigation settings
const tooltipBgColor = computed(() => navSettings.value.navigation?.backgroundColor || '#f093fb')
const tooltipTextColor = computed(() => navSettings.value.navigation?.textColor || '#ffffff')

// Load navigation settings
const loadNavSettings = async () => {
  try {
    const response = await settingsService.getNavigationSettings()
    if (response.success && response.data) {
      navSettings.value = response.data
    }
  } catch (error) {
    console.error('Error loading navigation settings:', error)
  }
}
const addingToCart = ref<number | null>(null)

// Methods
const loadProducts = async () => {
  loading.value = true
  try {
    const params = new URLSearchParams({
      limit: props.widget.limit.toString(),
      status: 'active'
    })

    if (props.widget.categoryId) {
      params.append('category_id', props.widget.categoryId.toString())
    }

    if (props.widget.brandId) {
      params.append('brand_id', props.widget.brandId.toString())
    }

    const response = await authFetch(`${API_BASE_URL}/products.php?${params}`)
    const data = await response.json()

    if (data.success && Array.isArray(data.data)) {
      products.value = data.data
    } else {
      products.value = []
    }
  } catch (error) {
    products.value = []
  } finally {
    loading.value = false
  }
}

const getProductImageUrl = (image: string | null) => {
  if (!image) return `${API_BASE_URL}/placeholder.php?width=300&height=300&text=No Product Image&bg=e9ecef&color=6c757d`
  if (image.startsWith('http')) return image
  return `${UPLOADS_BASE_URL}/products/${image}`
}

const handleImageError = (event: Event) => {
  const target = event.target as HTMLImageElement
  target.src = `${API_BASE_URL}/placeholder.php?width=300&height=300&text=Image Error&bg=f8d7da&color=721c24`
}

const formatPrice = (price: number) => {
  return price.toFixed(2)
}

const addToCart = async (product: Product) => {
  if (addingToCart.value === product.id) return
  
  addingToCart.value = product.id
  try {
    await cartStore.addToCart({
      id: product.id,
      name: product.name,
      price: product.sale_price || product.price,
      image: product.image,
      quantity: 1
    })
    
    // Show success feedback (optional)
  } catch (error) {
  } finally {
    addingToCart.value = null
  }
}

const getEmptyStateMessage = () => {
  if (props.widget.categoryId && props.widget.brandId) {
    return 'Δεν υπάρχουν προϊόντα σε αυτή την κατηγορία και μάρκα.'
  } else if (props.widget.categoryId) {
    return 'Δεν υπάρχουν προϊόντα σε αυτή την κατηγορία.'
  } else if (props.widget.brandId) {
    return 'Δεν υπάρχουν προϊόντα από αυτή τη μάρκα.'
  }
  return 'Δεν υπάρχουν διαθέσιμα προϊόντα.'
}

// Wishlist Methods
const isInWishlist = (productId: number) => {
  return wishlistStore.isInWishlist(productId)
}

const toggleWishlist = async (product: Product) => {
  try {
    if (isInWishlist(product.id)) {
      await wishlistStore.removeFromWishlist(product.id)
    } else {
      await wishlistStore.addToWishlist({
        id: product.id,
        name: product.name,
        price: product.sale_price || product.price,
        image: product.image,
        category: product.category_name
      })
    }
  } catch (error) {
  }
}

const quickView = (product: Product) => {
  // Navigate to product detail page
  router.push(`/products/${product.id}`)
}

// Lifecycle
onMounted(() => {
  loadNavSettings()
  loadProducts()
})
</script>

<style scoped>
.product-cards-widget {
  width: 100%;
}

.widget-title {
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 0;
  color: #2c3e50;
}

.product-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  overflow: visible;
  position: relative;
  transform: translateY(0) scale(1);
}

.product-card:hover {
  transform: scale(1.05);
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.2);
  z-index: 10;
}

.product-card:hover .product-title {
  white-space: normal;
  overflow: visible;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white !important;
  padding: 0.5rem;
  margin: -0.5rem -0.5rem 0.75rem -0.5rem;
  border-radius: 8px;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
  animation: titlePop 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

.product-card:hover .product-link {
  color: white !important;
}

@keyframes titlePop {
  0% {
    opacity: 0;
    transform: scale(0.8) translateY(-10px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.product-card::after {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, #0d6efd, #6610f2, #20c997, #fd7e14, #dc3545);
  background-size: 400% 400%;
  border-radius: 14px;
  z-index: -1;
  opacity: 0;
  animation: gradient-border 3s ease infinite;
  transition: opacity 0.3s ease;
}

.product-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.15) 50%, transparent 70%);
  transform: translateX(-100%);
  transition: transform 0.6s ease;
  pointer-events: none;
  z-index: 1;
  opacity: 0;
}

.product-card:hover {
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2), 
              0 0 0 1px rgba(13, 110, 253, 0.1),
              inset 0 1px 0 rgba(255, 255, 255, 0.2);
  transform: translateY(-8px) scale(1.03);
}

.product-card:hover::before {
  transform: translateX(100%);
  opacity: 1;
}

.product-card:hover::after {
  opacity: 0.7;
}

@keyframes gradient-border {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

/* Image Container */
.product-image-container {
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio - Square */
  overflow: hidden;
  background: #f8f9fa;
}

.product-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transition: all 0.5s cubic-bezier(0.23, 1, 0.320, 1);
  filter: brightness(1) contrast(1) saturate(1);
  display: block;
}

.product-card:hover .product-image {
  transform: scale(1.08) rotate(0.5deg);
  filter: brightness(1.1) contrast(1.05) saturate(1.1);
}

.product-badges {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.badge-sale {
  background: linear-gradient(135deg, #dc3545 0%, #e85d75 100%);
  color: white;
  animation: pulse-sale 2s infinite;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3);
}

.badge-new {
  background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
  color: #212529;
  animation: pulse-new 2s infinite;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3);
}

.badge-out-of-stock {
  background: #6c757d;
  color: white;
  opacity: 0.8;
}

@keyframes pulse-sale {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3), 0 0 0 0 rgba(220, 53, 69, 0.7);
  }
  50% {
    box-shadow: 0 2px 8px rgba(220, 53, 69, 0.3), 0 0 0 6px rgba(220, 53, 69, 0);
  }
}

@keyframes pulse-new {
  0%, 100% {
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3), 0 0 0 0 rgba(255, 193, 7, 0.7);
  }
  50% {
    box-shadow: 0 2px 8px rgba(255, 193, 7, 0.3), 0 0 0 6px rgba(255, 193, 7, 0);
  }
}

.product-category-badge {
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.9) 0%, rgba(102, 16, 242, 0.9) 100%);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  backdrop-filter: blur(15px);
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.product-card:hover .product-category-badge {
  transform: translateY(-3px) scale(1.05);
  box-shadow: 0 4px 15px rgba(13, 110, 253, 0.3);
}

/* Action Buttons */
.product-action-buttons {
  position: absolute;
  top: 12px;
  left: 12px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  opacity: 0;
  transform: translateX(-20px) translateY(-10px);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 2;
}

.product-card:hover .product-action-buttons {
  opacity: 1;
  transform: translateX(0) translateY(0);
}

.btn-icon {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #6c757d;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  backdrop-filter: blur(15px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.btn-icon::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: currentColor;
  transition: all 0.3s ease;
  transform: translate(-50%, -50%);
  opacity: 0.1;
}

.btn-icon:hover {
  background: white;
  transform: scale(1.15) rotate(5deg);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
}

.btn-icon:hover::before {
  width: 100%;
  height: 100%;
}

.wishlist-btn.active {
  background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(220, 53, 69, 0.4);
}

.wishlist-btn.active:hover {
  background: linear-gradient(135deg, #c82333 0%, #a71e2a 100%);
  transform: scale(1.15) rotate(-5deg);
  box-shadow: 0 8px 25px rgba(220, 53, 69, 0.6);
}

.quick-view-btn:hover {
  background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
  color: white;
  box-shadow: 0 8px 25px rgba(13, 110, 253, 0.4);
}

/* Content */
.product-content {
  padding: 1.5rem;
  position: relative;
  transition: all 0.3s ease;
}

.product-card:hover .product-content {
  transform: translateY(-2px);
}

.product-title {
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  transition: all 0.3s ease;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  cursor: pointer;
}

.product-title::after {
  content: attr(title);
  position: absolute;
  bottom: calc(100% + 15px);
  left: 50%;
  transform: translateX(-50%) scale(0.8);
  background: v-bind(tooltipBgColor);
  color: v-bind(tooltipTextColor);
  padding: 12px 20px;
  border-radius: 15px;
  font-size: 0.95rem;
  white-space: normal;
  max-width: 280px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3),
              0 0 0 1px rgba(255, 255, 255, 0.3) inset;
  opacity: 0;
  pointer-events: none;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 1000;
  line-height: 1.4;
  text-align: center;
  font-weight: 500;
  backdrop-filter: blur(10px);
}

.product-title::before {
  content: '';
  position: absolute;
  bottom: calc(100% + 5px);
  left: 50%;
  transform: translateX(-50%);
  width: 0;
  height: 0;
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-top: 10px solid v-bind(tooltipBgColor);
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 999;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.2));
}

.product-card:hover .product-title::after {
  opacity: 1;
  transform: translateX(-50%) scale(1);
  animation: tooltipBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.product-card:hover .product-title::before {
  opacity: 1;
}

@keyframes tooltipBounce {
  0% {
    opacity: 0;
    transform: translateX(-50%) scale(0.8) translateY(10px);
  }
  50% {
    transform: translateX(-50%) scale(1.05) translateY(-2px);
  }
  100% {
    opacity: 1;
    transform: translateX(-50%) scale(1) translateY(0);
  }
}

.product-card:hover .product-title {
  color: white !important;
  transform: translateX(0);
}

.product-link {
  color: #2c3e50;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  display: inline-block;
}

.product-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 2px;
  background: linear-gradient(90deg, #0d6efd, #6610f2);
  transition: width 0.3s ease;
}

.product-link:hover {
  color: #0d6efd;
  transform: translateY(-1px);
}

.product-link:hover::after {
  width: 100%;
}

.product-meta {
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
  color: #6c757d;
}

.product-description {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-clamp: 2;
  overflow: hidden;
}

/* Pricing */
.product-pricing {
  margin-bottom: 1rem;
}

.price-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.current-price,
.sale-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #28a745;
}

.original-price {
  font-size: 1rem;
  color: #6c757d;
  text-decoration: line-through;
}

/* Actions */
.product-actions {
  margin-top: auto;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Card Styles */
.style-minimal .product-card {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
}

.style-minimal .product-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.style-grid .product-card {
  border-radius: 8px;
}

.style-grid .product-image {
  height: 200px;
}

.style-grid .product-content {
  padding: 1rem;
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 3rem 1rem;
}

.loading-state .spinner-border {
  color: #0d6efd;
}

/* Responsive */
@media (max-width: 768px) {
  .products-grid .row {
    --bs-gutter-x: 1rem;
  }
  
  .product-content {
    padding: 1rem;
  }
  
  .widget-title {
    font-size: 1.2rem;
  }
  
  .product-image {
    height: 200px;
  }
}
</style>